import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../../../Redux/Store/Hooks";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.entry";

interface PreviewDialogProps {
  open: boolean;
  onClose: () => void;
}

const StudyMaterialPreviewDialog: React.FC<PreviewDialogProps> = ({ open, onClose }) => {
  const { data, isLoading } = useAppSelector(
    (state) => state.StudyMaterialPreviewSlice
  );

  const previewData = data as any;

  const [pdfPages, setPdfPages] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);


  useEffect(() => {
    const loadPdf = async () => {
      if (!previewData?.pdf_content) return;

      const pdfData = atob(previewData?.pdf_content);
      const pdfBytes = new Uint8Array(pdfData.length);
      for (let i = 0; i < pdfData.length; i++) {
        pdfBytes[i] = pdfData.charCodeAt(i);
      }



      const pdf = await pdfjsLib.getDocument({ data: pdfBytes }).promise;
      const pages: string[] = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d")!;
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;
        pages.push(canvas.toDataURL("image/png"));
      }

      setPdfPages(pages);
      setCurrentPage(0);
    };

    loadPdf();
  }, [previewData]);

  const goPrev = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const goNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, pdfPages.length - 1));
  };

  const handleDotClick = (index: number) => {
    setCurrentPage(index);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
     <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
    {/* Back button on left */}
    <IconButton
      onClick={onClose}
      sx={{ position: "absolute", left: 8 }} // absolute left
    >
      <ArrowBackIosNewIcon />
      <span style={{
        fontSize:"14px",
        marginLeft:"5px",
        color:"black"
      }}>Back to cart</span>
    </IconButton>

    {/* Title centered */}
    <Typography variant="h6" component="div" sx={{ textAlign: "center", flexGrow: 1 }}>
      {previewData?.file_name || "Study Material Preview"}
    </Typography>
  </DialogTitle>

      <DialogContent
        dividers
        style={{
          height: "70vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : pdfPages.length > 0 ? (
          <>
            <Box
            height={"60vh"}
              display="flex"
              justifyContent="center"
              alignItems="center"
              flex={1}
              width="100%"
              sx={{ maxHeight: "70vh", position: "relative" }}
            >
              {/* Left Arrow */}
              <IconButton
                onClick={goPrev}
                disabled={currentPage === 0}
                sx={{
                  position: "absolute",
                  left: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(0,0,0,0.3)",
                  color: "white",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.5)" },
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>

              {/* PDF Page */}
              <img
                src={pdfPages[currentPage]}
                alt={`Page ${currentPage + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />

              {/* Right Arrow */}
              <IconButton
                onClick={goNext}
                disabled={currentPage === pdfPages.length - 1}
                sx={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(0,0,0,0.3)",
                  color: "white",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.5)" },
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </Box>

            {/* Dots Navigation */}
            <Box mt={2} display="flex" justifyContent="center" gap={1}>
              {pdfPages.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => handleDotClick(index)}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: currentPage === index ? "primary.main" : "grey.400",
                    cursor: "pointer",
                  }}
                />
              ))}
            </Box>
          </>
        ) : (
          <Typography textAlign="center">No file to preview</Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained"          
         sx={{
            fontSize: "14px",
            color: "#ffffff",
            bgcolor: "#009DFF",
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": {
              opacity: 0.8,
              bgcolor: "#009DFF",
            },
          }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudyMaterialPreviewDialog;
